import {
  MailOutlined,
  MoreOutlined,
  QuestionCircleFilled
} from "@ant-design/icons";
import {
  Button,
  Collapse,
  Dropdown,
  Empty,
  Menu,
  Modal,
  Skeleton,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR, { mutate } from "swr";
import PositionsTag from "../../components/PositionsTag";
import SurveyCampaignStatusTag from "../../components/SurveyCampaignStatusTag";
import SurveyResponseStatusTag from "../../components/SurveyResponseStatusTag";
import {
  DeleteSurveyCampaignRequest,
  GetSurveyCampaignBySurveyCampaignIdRequest,
  GetSurveyResponseBySurveyCampaignIdResponse,
  GetSurveyTemplatesBySurveyTemplateIdRequest,
  ResendSurveyResponseEmailRequest,
  UndeleteSurveyCampaignRequest,
} from "../../constants/apiRequestResponse";
import TEST_IDS from "../../constants/cypressTestIds";
import { COLORS } from "../../constants/misc";
import {
  FeatureFlagStatus,
  FeatureFlagTemplateKey,
  SurveyCampaign,
  SurveyCampaignStatusLabels,
  SurveyResponse,
  SurveyResponseStatusLabels,
  SurveyTemplate,
  SurveyTemplateType
} from "../../constants/types";
import {
  deleteSurveyCampaign,
  fetcher,
  finishSurveyCampaign,
  getSurveyCampaignBySurveyCampaignId,
  getSurveyTemplateBySurveyTemplateId,
  resendSurveyResponseEmail,
  startSurveyCampaign,
  undeleteSurveyCampaign,
} from "../../services/api";
import { SurveyCampaignKeys } from "../../services/i8tn/SurveyCampaignsPage/keys";
import { SurveyCampaignViewKeys } from "../../services/i8tn/SurveyCampaignView/keys";
import { getQueryParam, parseTime } from "../../util/index";
import { SessionContext } from "../../App";

interface Prop {
  onSurveyCampaignUpdated: (surveyCampaign: SurveyCampaign) => void;
}

type QueryValues = {
  surveySearchString: string;
  surveyStatus: string;
  surveyCampaignId?: string;
};

export default function SurveyCampaignView(props: Prop) {
  const surveyCampaignId = getQueryParam("surveyCampaignId");
   const context = useContext(SessionContext);
   
  const { data: getSurveyResponseRes, isLoading: isSurveyResponseLoading } =
    useSWR<GetSurveyResponseBySurveyCampaignIdResponse>(
      `/surveyResponse/getBySurveyCampaignId/${surveyCampaignId}`,
      fetcher,
      {
        refreshInterval: () => {
          const hasUnsentEmails = getSurveyResponseRes?.surveyResponses?.find(
            (sr) => sr.status === SurveyResponseStatusLabels.PENDING
          );
          if (hasUnsentEmails) {
            return 1 * 1000;
          }
          return 5 * 1000;
        },
      }
    );

  const surveyResponses = getSurveyResponseRes?.surveyResponses;

  const [isLoading, setIsLoading] = useState(false);
  const [surveyCampaign, setSurveyCampaign] = useState<
    SurveyCampaign | undefined
  >();
  const [surveyTemplate, setSurveyTemplate] = useState<
    SurveyTemplate | undefined
  >();
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<string[]>([]);
  const { t } = useTranslation();

  const queryValues: QueryValues = {
    surveySearchString: getQueryParam("surveySearchString") || "",
    surveyStatus: getQueryParam("surveyStatus") || "All",
    surveyCampaignId: getQueryParam("surveyCampaignId") || "",
  };

  const getData = useCallback(async () => {
    if (!surveyCampaignId) return null;
    if (surveyCampaignId === surveyCampaign?._id) return null;

    try {

      const req1: GetSurveyCampaignBySurveyCampaignIdRequest = {
        surveyCampaignId: surveyCampaignId,
      };

      const minimumDurationBeforeShowingLoaderInMs = 500
      const loadingTimeout = setTimeout(() => {
        setIsLoading(true);
      }, minimumDurationBeforeShowingLoaderInMs);
      const res1 = await getSurveyCampaignBySurveyCampaignId(req1, true);
      clearTimeout(loadingTimeout);

      if (!res1.success) throw res1.message;
      setSurveyCampaign(res1.surveyCampaign);

      setIsLoading(false);

      const req2: GetSurveyTemplatesBySurveyTemplateIdRequest = {
        surveyTemplateId: res1.surveyCampaign.surveyTemplateId,
      };
      const res2 = await getSurveyTemplateBySurveyTemplateId(req2);
      if (!res2.success) throw res2.message;
      setSurveyTemplate(res2.surveyTemplate);
    } catch (err) {
      message.error(
        typeof err === "string"
          ? err
          : typeof err === "object"
          ? err?.toString()
          : t(SurveyCampaignViewKeys.surveyCampaignView_somethingWentWrongString)
      );
      setIsLoading(false);
    }
  }, [surveyCampaignId, surveyCampaign?._id, t]);

  useEffect(() => {
    getData();
  }, [getData, surveyCampaignId]);

  if (!surveyCampaignId) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        <Typography.Title level={5}>
          <>{t(SurveyCampaignKeys.surveyCampaigns_selectFromLeftString)}</>
        </Typography.Title>
      </div>
    );
  }

  function onResendSurveyResponses(surveyIds: string[]) {
    const surveysToResend =
      surveyIds
        .map((surveyResponseId) =>
          surveyResponses?.find((s) => s._id === surveyResponseId)
        )
        .filter((survey) => {
          return survey?._isAbleToResend;
        }) || [];

    Modal.confirm({
      title:
        surveysToResend.length > 0
          ? t(SurveyCampaignViewKeys.surveyCampaignView_confirmResendEmailPrompt)
          : t(SurveyCampaignViewKeys.surveyCampaignView_selectedSurveysCannotBeResentString),
      content:
        surveysToResend.length > 0 ? (
          <>
            <p>{t(SurveyCampaignViewKeys.surveyCampaignView_emailsWouldBeSentOutString, { count: surveysToResend.length })}</p>
            <ul>
              {surveysToResend.map((s) => {
                return <li key={s?._id}>{s?.surveyEmployee.email}</li>;
              })}
            </ul>
          </>
        ) : null,
      onOk: () => {
        const req: ResendSurveyResponseEmailRequest = {
          surveyResponseIds: surveysToResend.map((s) => s?._id || ""),
        };
        resendSurveyResponseEmail(req)
          .then((res) => {
            if (!res.success) throw new Error(res.message);
            message.success(t(SurveyCampaignViewKeys.surveyCampaignView_resendScheduledPrompt));

            mutate(
              `/surveyResponse/getBySurveyCampaignId/${getQueryParam(
                "surveyCampaignId"
              )}`
            );
          })
          .catch((err) => message.error(err.message));
      },
    });
  }

  function onComplete(surveyCampaignId: string | undefined) {
    if (surveyCampaignId === undefined) {
      message.error(t(SurveyCampaignViewKeys.surveyCampaignView_invalidSurveyCampaignString));
    } else {
      message.loading(
        <>
          {t(
            SurveyCampaignViewKeys.surveyCampaignView_completingSurveyCampaignPrompt
          )}
        </>,
        1
      );

      finishSurveyCampaign(surveyCampaignId)
        .then((res) => {
          const req1: GetSurveyCampaignBySurveyCampaignIdRequest = {
            surveyCampaignId: surveyCampaignId,
          };

          return getSurveyCampaignBySurveyCampaignId(req1);
        })
        .then((res) => {
          if (!res.success) throw new Error(res.message);

          setSurveyCampaign(res.surveyCampaign);
          props.onSurveyCampaignUpdated(res.surveyCampaign);

          message.success(
            <>
              {t(
                SurveyCampaignViewKeys.surveyCampaignView_completedSurveyCampaignPrompt
              )}
            </>
          );
        })
        .catch((err) => message.error(err.message));
    }
  }

  function onStart(surveyCampaignId: string | undefined) {
    if (surveyCampaignId === undefined) {
      message.error(t(SurveyCampaignViewKeys.surveyCampaignView_invalidSurveyCampaignString));
    } else {
      message.loading(t(SurveyCampaignViewKeys.surveyCampaignView_startingSurveyCampaignPrompt), 1);

      startSurveyCampaign(surveyCampaignId)
        .then(() => {
          const req1: GetSurveyCampaignBySurveyCampaignIdRequest = {
            surveyCampaignId: surveyCampaignId,
          };

          return getSurveyCampaignBySurveyCampaignId(req1);
        })
        .then((res) => {
          if (!res.success) throw new Error(res.message);

          setSurveyCampaign(res.surveyCampaign);
          props.onSurveyCampaignUpdated(res.surveyCampaign);

          message.success(t(SurveyCampaignViewKeys.surveyCampaignView_startedSurveyCampaignPrompt));
        })
        .catch((err) => message.error(err));
    }
  }

  function onDelete(surveyCampaignId: string | undefined) {
    if (surveyCampaignId === undefined) {
      message.error(t(SurveyCampaignViewKeys.surveyCampaignView_invalidSurveyCampaignString));
    } else {
      message.loading(
        <>
          {t(
            SurveyCampaignViewKeys.surveyCampaignView_deletingSurveyCampaignPrompt
          )}
        </>,
        1
      );

      const req: DeleteSurveyCampaignRequest = {
        surveyCampaignId: surveyCampaignId,
      };
      deleteSurveyCampaign(req)
        .then(() => {
          const req1: GetSurveyCampaignBySurveyCampaignIdRequest = {
            surveyCampaignId: surveyCampaignId,
          };

          return getSurveyCampaignBySurveyCampaignId(req1);
        })
        .then((res) => {
          if (!res.success) throw new Error(res.message);

          setSurveyCampaign(res.surveyCampaign);
          props.onSurveyCampaignUpdated(res.surveyCampaign);

          message.success(
            <>
              {t(
                SurveyCampaignViewKeys.surveyCampaignView_deletedSurveyCampaignPrompt
              )}
            </>
          );
        })
        .catch((err) => message.error(err.message));
    }
  }

  function onUndelete(surveyCampaignId: string | undefined) {
    if (surveyCampaignId === undefined) {
      message.error(t(SurveyCampaignViewKeys.surveyCampaignView_invalidSurveyCampaignString));
    } else {
      message.loading(
        <>
          {t(
            SurveyCampaignViewKeys.surveyCampaignView_restoringSurveyCampaignPrompt
          )}
        </>,
        1
      );

      const req: UndeleteSurveyCampaignRequest = {
        surveyCampaignId: surveyCampaignId,
      };
      undeleteSurveyCampaign(req)
        .then((res) => {
          if (!res.success) throw new Error(res.message);

          const req1: GetSurveyCampaignBySurveyCampaignIdRequest = {
            surveyCampaignId: surveyCampaignId,
          };

          return getSurveyCampaignBySurveyCampaignId(req1);
        })
        .then((res) => {
          if (!res.success) throw new Error(res.message);

          setSurveyCampaign(res.surveyCampaign);
          props.onSurveyCampaignUpdated(res.surveyCampaign);

          message.success(
            <>
              {t(
                SurveyCampaignViewKeys.surveyCampaignView_restoredSurveyCampaignPrompt
              )}
            </>
          );
        })
        .catch((err) => message.error(err.message));
    }
  }

  const columns = [
    {
      title: (
        <>{t(SurveyCampaignViewKeys.surveyCampaignView_emailTableHeader)}</>
      ),
      render: (rowData: SurveyResponse) => {
        return <span>{rowData.surveyEmployee.email}</span>;
      },
    },
    {
      title: (
        <>{t(SurveyCampaignViewKeys.surveyCampaignView_statusTableHeader)}</>
      ),
      render: (rowData: SurveyResponse) => {
        return <SurveyResponseStatusTag status={rowData.status} />;
      },
    },
    {
      title: (
        <span>
          {t(SurveyCampaignViewKeys.surveyCampaignView_progressTableHeader)}{" "}
          &nbsp;
          <Tooltip
            title={t(SurveyCampaignViewKeys.surveyCampaignView_progressPrompt)}
          >
            <QuestionCircleFilled />
          </Tooltip>
        </span>
      ),
      render: (rowData: SurveyResponse) => {
        return `${rowData._totalNumberOfAnsweredQuestions} / ${rowData._totalNumberOfQuestions}`;
      },
    },
    {
      title: (
        <>{t(SurveyCampaignViewKeys.surveyCampaignView_firstNameTableHeader)}</>
      ),
      render: (rowData: SurveyResponse) => {
        return <span>{rowData.surveyEmployee.fname}</span>;
      },
    },
    {
      title: (
        <>{t(SurveyCampaignViewKeys.surveyCampaignView_lastNameTableHeader)}</>
      ),
      render: (rowData: SurveyResponse) => {
        return <span>{rowData.surveyEmployee.lname}</span>;
      },
    },
    {
      title: (
        <>{t(SurveyCampaignViewKeys.surveyCampaignView_positionTableHeader)}</>
      ),
      render: (rowData: SurveyResponse) => {
        return <PositionsTag position={rowData.surveyEmployee.position} />;
      },
    },
    {
      title: (
        <>{t(SurveyCampaignViewKeys.surveyCampaignView_teamTableHeader)}</>
      ),
      render: (rowData: SurveyResponse) => {
        return <span>{rowData.surveyEmployee.team}</span>;
      },
    },
    {
      title: (
        <>{t(SurveyCampaignViewKeys.surveyCampaignView_genderTableHeader)}</>
      ),
      render: (rowData: SurveyResponse) => {
        return <span>{rowData.surveyEmployee.gender}</span>;
      },
    },
    {
      title: (
        <>
          {t(SurveyCampaignViewKeys.surveyCampaignView_lastUpdatedTableHeader)}
        </>
      ),
      dataIndex: "updatedAt",
      render: (updatedAt) => parseTime(updatedAt),
    },
    {
      title: (
        <>{t(SurveyCampaignViewKeys.surveyCampaignView_idTableHeader)}</>
      ),
      dataIndex: "_id",
    },
  ];

  const dataSource =
    surveyResponses?.filter((sr) => {
      /**
       * 12/6/2022 daniel.kwok
       * Disabled search or filter by api. Frontend only, temporarily
       */

      let isIncludeSearchStringMatch = true;
      let isSurveyCampaignStatusMatch = true;

      if (queryValues.surveySearchString) {
        const hashedString = `${sr.surveyEmployee.email} ${sr.surveyEmployee.fname} ${sr.surveyEmployee.lname} ${sr.surveyEmployee.position} ${sr.surveyEmployee.team}`;
        isIncludeSearchStringMatch = hashedString
          .toLocaleLowerCase()
          .includes(queryValues.surveySearchString.toLowerCase());
      }

      if (queryValues.surveyStatus && queryValues.surveyStatus !== "All") {
        isSurveyCampaignStatusMatch = sr.status === queryValues.surveyStatus;
      }

      return isIncludeSearchStringMatch && isSurveyCampaignStatusMatch;
    }) || [];

  function getSurveyStatisticComponent(label: string, value: any) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <p style={{ color: "grey", marginBottom: 0 }}>{label}</p>
        <p>{value}</p>
      </div>
    );
  }

  function getActionsComponent(selectedSurveyIds: string[]) {
    const cannotModifySurveyCampaign = surveyCampaign?.status !== SurveyCampaignStatusLabels.ongoing

    return (
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          marginBottom: 10,
          justifyContent: "space-between",
          width: "100%",
        }}
      >            
            <div
              style={{
                display: "flex",
                gap: 10,
              }}
            >
              <Button
                onClick={(e) => {
                  onResendSurveyResponses(selectedSurveyIds);
                }}
                disabled={cannotModifySurveyCampaign || selectedSurveyIds.length === 0}
                icon={<MailOutlined />}
              >
                {t(SurveyCampaignViewKeys.surveyCampaignView_resendEmailBtn)}
              </Button>
            </div>
      </div>
    );
  }

  function getTableComponent() {
    if (!surveyResponses && isSurveyResponseLoading) {
      return <Skeleton active />;
    }
    switch (surveyTemplate?.type) {
      case SurveyTemplateType.feedback:
        const participants: SurveyResponse[] = dataSource.filter(
          (d) => d.surveyEmployee._id === d.surveyEmployee.participantId
        );

        if (participants.length === 0) {
          return <Empty />;
        }

        return (
          <>
            {participants.map((participant, i) => {
              const peers = dataSource.filter((d) => {
                const isPeer =
                  d.surveyEmployee.participantId ===
                  participant.surveyEmployee._id;
                return isPeer;
              });

              return (
                <div key={participant._id}>
                  <Typography.Title level={5}>
                    {t(SurveyCampaignViewKeys.surveyCampaignView_360SurveyString, { number: i + 1 })}{" "}
                    {`${participant.surveyEmployee.fname} ${participant.surveyEmployee.lname}(${participant.surveyEmployee.email})`}
                  </Typography.Title>
                  <Table
                    rowKey="_id"
                    columns={columns}
                    dataSource={peers}
                    scroll={{
                      x: columns.length * 150,
                    }}
                    rowSelection={
                      surveyCampaign?.status ===
                        SurveyCampaignStatusLabels.ongoing
                        ? {
                          type: "checkbox",
                          selectedRowKeys: selectedSurveyIds,
                          onChange: (
                            selectedRowKeys,
                            selectedRows: SurveyResponse[]
                          ) => {
                            setSelectedSurveyIds(selectedRowKeys as string[]);
                          },
                          onSelectAll: () => {
                            setSelectedSurveyIds(
                              surveyResponses?.map((s) => s._id) || []
                            );
                          },
                        }
                        : undefined
                    }
                    pagination={{
                      position: ["bottomRight"],
                      showSizeChanger: true,
                      defaultPageSize: 10,
                    }}
                  />
                </div>
              );
            })}
            {getFAQComponent()}
          </>
        );
      default:
        return (
          <>
            <Table
              rowKey="_id"
              columns={columns}
              dataSource={dataSource}
              /**
               * 9/9/2022 daniel.kwok
               * Hide checkboxes if campaign hasn't started
               */
              rowSelection={
                surveyCampaign?.status === SurveyCampaignStatusLabels.ongoing
                  ? {
                    type: "checkbox",
                    selectedRowKeys: selectedSurveyIds,
                    onChange: (
                      selectedRowKeys,
                      selectedRows: SurveyResponse[]
                    ) => {
                      setSelectedSurveyIds(selectedRowKeys as string[]);
                    },
                    onSelectAll: () => {
                      setSelectedSurveyIds(
                        surveyResponses?.map((s) => s._id) || []
                      );
                    },
                  }
                  : undefined
              }
              scroll={{
                x: columns.length * 150,
              }}
              pagination={{
                position: ["bottomRight"],
                showSizeChanger: true,
                defaultPageSize: 10,
              }}
            />
            {getFAQComponent()}
          </>
        );
    }
  }

  function getTitleComponent(surveyCampaign?: SurveyCampaign) {
    const isEnableViewReport = context?.featureFlags?.find((ff) => {
        return (
          ff.key === FeatureFlagTemplateKey.ENABLE_PREMIUM_FEATURES &&
          ff.status === FeatureFlagStatus.ENABLED
        );
      })
        ? true
        : false;

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            {surveyCampaign?.name}
          </Typography.Title>
          <p style={{ color: "grey" }}>ID: {surveyCampaign?._id}</p>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          {surveyCampaign?.status === SurveyCampaignStatusLabels.draft ? (
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
              }}
            >
              <Button
                href={`/surveycampaign/edit?surveyCampaignId=${surveyCampaign?._id}`}
              >
                {t(SurveyCampaignViewKeys.surveyCampaignView_editBtn)}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    title: t(SurveyCampaignViewKeys.surveyCampaignView_completeCampaignAreYouSurePrompt),
                    content: <p>{t(SurveyCampaignViewKeys.surveyCampaignView_emailsWouldBeSentToAllRecipientsString)}</p>,
                    onOk: () => onStart(surveyCampaignId),
                  });
                }}
              >
                {t(SurveyCampaignViewKeys.surveyCampaignView_startCampaignBtn)}
              </Button>
            </div>
          ) : surveyCampaign?.status === SurveyCampaignStatusLabels.ongoing ? (
            <>
              <Button
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    title: (
                      <>
                        {t(
                          SurveyCampaignViewKeys.surveyCampaignView_completeCampaignAreYouSurePrompt
                        )}
                      </>
                    ),
                    onOk: () => onComplete(surveyCampaignId),
                  });
                }}
              >
                {t(
                  SurveyCampaignViewKeys.surveyCampaignView_completeCampaignBtn
                )}
              </Button>
              {isEnableViewReport ? (
                <a
                  href={`/report/view?surveyCampaignId=${surveyCampaignId}&surveyTemplateType=${surveyTemplate?.type}`}
                >
                  <Button type="link">
                    {t(SurveyCampaignViewKeys.surveyCampaignView_viewReportBtn)}
                  </Button>
                </a>
              ) : (
                <Tooltip
                  title={t(
                    SurveyCampaignViewKeys.surveyCampaignView_isDisableReportReadWarnString
                  )}
                >
                  <Button disabled={true} type="link">
                    {t(SurveyCampaignViewKeys.surveyCampaignView_viewReportBtn)}
                  </Button>
                </Tooltip>
              )}
            </>
          ) : surveyCampaign?.status ===
            SurveyCampaignStatusLabels.completed ? (
            isEnableViewReport ? (
              <a
                href={`/report/view?surveyCampaignId=${surveyCampaignId}&surveyTemplateType=${surveyTemplate?.type}`}
              >
                <Button type="primary">
                  {t(SurveyCampaignViewKeys.surveyCampaignView_viewReportBtn)}
                </Button>
              </a>
            ) : (
              <Tooltip
                title={t(
                  SurveyCampaignViewKeys.surveyCampaignView_isDisableReportReadWarnString
                )}
              >
                <Button disabled={true} type="primary">
                  {t(SurveyCampaignViewKeys.surveyCampaignView_viewReportBtn)}
                </Button>
              </Tooltip>
            )
          ) : null}

          <Dropdown
            overlay={
              <Menu>
                {surveyCampaign?.status ===
                SurveyCampaignStatusLabels.deleted ? (
                  <Menu.Item
                    key="/restore"
                    onClick={() => {
                      Modal.confirm({
                        title: (
                          <>
                            {t(
                              SurveyCampaignViewKeys.surveyCampaignView_completeCampaignAreYouSurePrompt
                            )}
                          </>
                        ),
                        content: t(
                          SurveyCampaignViewKeys.surveyCampaignView_restoreCampaignPrompt
                        ),
                        onOk: () => onUndelete(surveyCampaignId),
                      });
                    }}
                  >
                    {t(SurveyCampaignViewKeys.surveyCampaignView_restoreBtn)}
                  </Menu.Item>
                ) : (
                  <Menu.Item
                    key="/delete"
                    onClick={() => {
                      Modal.confirm({
                        title: (
                          <>
                            {t(
                              SurveyCampaignViewKeys.surveyCampaignView_completeCampaignAreYouSurePrompt
                            )}
                          </>
                        ),
                        content: t(
                          SurveyCampaignViewKeys.surveyCampaignView_deleteCampaignPrompt
                        ),
                        onOk: () => onDelete(surveyCampaignId),
                      });
                    }}
                  >
                    {t(SurveyCampaignViewKeys.surveyCampaignView_deleteBtn)}
                  </Menu.Item>
                )}
              </Menu>
            }
            trigger={["click"]}
            placement="bottomLeft"
          >
            <Tooltip
              title={t(SurveyCampaignViewKeys.surveyCampaignView_moreTooltip)}
            >
              <MoreOutlined
                data-testid={`${TEST_IDS.SURVEY_CAMPAIGN_VIEW.more_id}`}
                style={{
                  color: COLORS.PRIMARY_COLOR,
                }}
              />
            </Tooltip>
          </Dropdown>
        </div>
      </div>
    );
  }

  function getFAQComponent() {
    return (
      <>
        <Typography.Title level={3}>
          {t(SurveyCampaignViewKeys.surveyCampaignView_faqString)}
        </Typography.Title>
        <Collapse>
          <Collapse.Panel
            header={t(
              SurveyCampaignViewKeys.surveyCampaignView_availableStatusString
            )}
            key="1"
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <div>
                <SurveyResponseStatusTag
                  status={SurveyResponseStatusLabels.PENDING}
                />
                <p>
                  {t(
                    SurveyCampaignViewKeys.surveyCampaignView_pendingStatusString
                  )}
                </p>
              </div>
              <div>
                <SurveyResponseStatusTag
                  status={SurveyResponseStatusLabels.EMAIL_SENT}
                />
                <p>
                  {t(
                    SurveyCampaignViewKeys.surveyCampaignView_sentStatusString
                  )}
                </p>
              </div>

              <div>
                <SurveyResponseStatusTag
                  status={SurveyResponseStatusLabels.EMAIL_FAILED}
                />
                <p>
                  {t(
                    SurveyCampaignViewKeys.surveyCampaignView_failedStatusString
                  )}
                </p>
              </div>
              <div>
                <SurveyResponseStatusTag
                  status={SurveyResponseStatusLabels.CANCELLED}
                />
                <p>
                  {t(
                    SurveyCampaignViewKeys.surveyCampaignView_cancelledStatusString
                  )}
                </p>
              </div>
              <div>
                <SurveyResponseStatusTag
                  status={SurveyResponseStatusLabels.COMPLETED}
                />
                <p>
                  {t(
                    SurveyCampaignViewKeys.surveyCampaignView_completedStatusString
                  )}
                </p>
              </div>
            </div>
          </Collapse.Panel>
        </Collapse>
      </>
    );
  }

  if (isLoading) {
    return <Skeleton active />;
  }
  return (
    <>
      {getTitleComponent(surveyCampaign)}

      <br />
      <div
        style={{
          display: 'grid',
          width: '40%',
          gridTemplateColumns: 'repeat(3, 1fr)',
        }}
      >
        {getSurveyStatisticComponent(
          t(SurveyCampaignKeys.surveyCampaigns_statusString),
          <SurveyCampaignStatusTag status={surveyCampaign?.status} />
        )}
        {getSurveyStatisticComponent(
          t(SurveyCampaignKeys.surveyCampaigns_surveyString),
          surveyTemplate?.name
        )}
        {getSurveyStatisticComponent(
          t(SurveyCampaignViewKeys.surveyCampaignView_createdString),
          parseTime(surveyCampaign?.createdAt)
        )}
      </div>

      <br />
      <div
        style={{
          display: "flex",
          gap: 10,
        }}
      >
        {getActionsComponent(selectedSurveyIds)}
      </div>

      {getTableComponent()}
    </>
  );
}
